import { useEffect, useRef, useState } from 'react';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error(data?.error || 'Request failed');
    return data;
  });
}

const HIGH_ENERGY_OPTIONS = [
  { key: 'electrical',      label: 'Electrical Energy' },
  { key: 'hydraulic',       label: 'Hydraulic / Pneumatic' },
  { key: 'chemical',        label: 'Chemical / Hazmat' },
  { key: 'gravity',         label: 'Gravity / Stored Energy' },
  { key: 'thermal',         label: 'Thermal' },
  { key: 'kinetic',         label: 'Kinetic / Moving Parts' },
  { key: 'radiation',       label: 'Radiation' },
];

const PPE_OPTIONS = [
  { key: 'hard_hat',        label: 'Hard Hat' },
  { key: 'safety_glasses',  label: 'Safety Glasses' },
  { key: 'hi_vis_vest',     label: 'Hi-Vis Vest' },
  { key: 'gloves',          label: 'Gloves' },
  { key: 'steel_toe_boots', label: 'Steel Toe Boots' },
  { key: 'face_shield',     label: 'Face Shield' },
  { key: 'hearing_protect', label: 'Hearing Protection' },
  { key: 'fall_harness',    label: 'Fall Harness' },
  { key: 'arc_flash_ppe',   label: 'Arc Flash PPE' },
  { key: 'ppe_other',       label: 'Other' },
];

const GENERAL_HAZARDS = [
  { key: 'working_at_heights',  label: 'Working at Heights' },
  { key: 'confined_space',      label: 'Confined Space' },
  { key: 'hot_work',            label: 'Hot Work' },
  { key: 'live_power',          label: 'Live Power Present' },
  { key: 'moving_traffic',      label: 'Moving Traffic' },
  { key: 'slip_trip_fall',      label: 'Slip / Trip / Fall' },
  { key: 'heavy_lift',          label: 'Heavy Lifting' },
  { key: 'excavation',          label: 'Excavation / Trenching' },
  { key: 'environmental',       label: 'Environmental Hazard' },
];

const HOT_WEATHER_ITEMS = [
  { key: 'water_available',     label: 'Water Available (≥1 qt/hr)' },
  { key: 'shade_accessible',    label: 'Shade / Cool Area Accessible' },
  { key: 'heat_illness_plan',   label: 'Heat Illness Prevention Plan Reviewed' },
  { key: 'buddy_system',        label: 'Buddy System in Place' },
  { key: 'heat_index_checked',  label: 'Heat Index Checked' },
];

const LOGGED_IN_OPTIONS = ['TDC', 'DDC', 'NOC', 'CICO', 'other'];

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 42, height: 24, borderRadius: 12,
          backgroundColor: checked ? '#2563eb' : '#334155',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: '50%', backgroundColor: 'white',
          position: 'absolute', top: 3, left: checked ? 21 : 3, transition: 'left 0.2s',
        }} />
      </div>
      <span style={{ color: '#e2e8f0', fontSize: 13 }}>{label}</span>
    </label>
  );
}

function CheckGroup({ options, selected, onChange }) {
  const toggle = (key) => {
    const next = selected.includes(key)
      ? selected.filter(k => k !== key)
      : [...selected, key];
    onChange(next);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map(o => (
        <button key={o.key} onClick={() => toggle(o.key)} style={{
          padding: '5px 11px', borderRadius: 6, fontSize: 13,
          border: `1px solid ${selected.includes(o.key) ? '#2563eb' : '#334155'}`,
          backgroundColor: selected.includes(o.key) ? '#1e3a5f' : '#111827',
          color: selected.includes(o.key) ? '#93c5fd' : '#94a3b8',
          cursor: 'pointer',
        }}>{o.label}</button>
      ))}
    </div>
  );
}

function SectionHead({ title }) {
  return (
    <div style={{ padding: '9px 14px', backgroundColor: '#111827', color: '#cbd5e1', fontSize: 13, fontWeight: 800, borderBottom: '1px solid #1e293b', marginBottom: 0 }}>
      {title}
    </div>
  );
}

function SectionBody({ children }) {
  return <div style={{ padding: 14 }}>{children}</div>;
}

function Field({ label, children, span }) {
  return (
    <label style={{ display: 'block', gridColumn: span ? '1 / -1' : undefined }}>
      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 5 }}>{label}</div>
      {children}
    </label>
  );
}

const inp = {
  padding: '8px 10px', backgroundColor: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', fontSize: 13, width: '100%', boxSizing: 'border-box',
};

const ta = { ...inp, minHeight: 72, resize: 'vertical' };

// Signature canvas — draws on touch/mouse
function SignatureCanvas({ onSave }) {
  const ref = useRef(null);
  const drawing = useRef(false);

  const pos = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const src = e.touches?.[0] ?? e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const down = (e) => { drawing.current = true; const p = pos(e); const ctx = ref.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); const ctx = ref.current.getContext('2d'); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const up = () => { drawing.current = false; };

  const clear = () => {
    const ctx = ref.current.getContext('2d');
    ctx.clearRect(0, 0, ref.current.width, ref.current.height);
  };

  const save = () => {
    const data = ref.current.toDataURL('image/png');
    onSave(data);
  };

  useEffect(() => {
    const ctx = ref.current.getContext('2d');
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  return (
    <div>
      <canvas
        ref={ref} width={400} height={120}
        style={{ border: '1px solid #334155', borderRadius: 6, backgroundColor: '#0f172a', cursor: 'crosshair', touchAction: 'none', display: 'block' }}
        onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
        onTouchStart={down} onTouchMove={move} onTouchEnd={up}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button onClick={clear} style={{ padding: '6px 14px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Clear</button>
        <button onClick={save} style={{ padding: '6px 14px', backgroundColor: '#2563eb', border: 'none', color: 'white', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Use Signature</button>
      </div>
    </div>
  );
}

// Inline sign row for one crew member
function SignRow({ jhaId, teamUsers, onSigned }) {
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const sign = async () => {
    setErr('');
    setBusy(true);
    try {
      await api(`/jhas/${jhaId}/sign`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId ? Number(userId) : undefined,
          printed_name: name || teamUsers.find(u => String(u.id) === userId)?.name || '',
          company,
          pin: pin || undefined,
        }),
      });
      setUserId(''); setName(''); setCompany(''); setPin('');
      onSigned();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const selectedUser = teamUsers.find(u => String(u.id) === userId);
  const displayName = userId ? (selectedUser?.name || '') : name;

  return (
    <div style={{ border: '1px solid #1e293b', borderRadius: 8, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.3fr 1fr 1.4fr auto', gap: 8, alignItems: 'flex-end' }}>
        <Field label="Team Member">
          <select value={userId} onChange={e => { setUserId(e.target.value); setName(''); }} style={inp}>
            <option value="">— Manual / Contractor —</option>
            {teamUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        <Field label="Printed Name">
          <input value={userId ? (selectedUser?.name || '') : name} onChange={e => setName(e.target.value)} readOnly={!!userId} style={inp} placeholder="Full name" />
        </Field>
        <Field label="Company">
          <input value={company} onChange={e => setCompany(e.target.value)} style={inp} placeholder="Optional" />
        </Field>
        <Field label="Signature">
          <input type="password" inputMode="numeric" value={pin} onChange={e => setPin(e.target.value)} style={inp} placeholder="Use JHA PIN for signature" />
        </Field>
        <button onClick={sign} disabled={busy || !displayName} style={{ padding: '9px 16px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13, height: 'fit-content' }}>
          {busy ? '…' : 'Sign'}
        </button>
      </div>
      {err && <div style={{ color: '#f87171', fontSize: 12, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

export default function JHAForm({ workItemId, onClose }) {
  const [jha, setJha] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [step, setStep] = useState('prejob'); // prejob | signing | postjob | done
  const [form, setForm] = useState({
    logged_in_to: '', dispatcher: '',
    high_energy_hazards: [], hazards: [],
    change_management_completed: null, change_outage_number: '',
    critical_steps: '',
    ppe_selected: [],
    allergy_awareness: '', allergy_type: '', allergy_action_plan: '',
    general_hazards: [],
    hot_weather_applicable: false, hot_weather_checklist: [],
  });
  const [postJob, setPostJob] = useState({ logged_out: null, job_performed_as_expected: null, post_job_comments: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Load or create JHA, load team roster
  useEffect(() => {
    if (!workItemId) return;
    Promise.all([
      api(`/jhas?work_item_id=${workItemId}`),
      api('/jhas/crew').catch(() => []),
    ]).then(([jhas, crew]) => {
      setTeamUsers(Array.isArray(crew) ? crew : []);
      const existing = Array.isArray(jhas) && jhas[0];
      if (existing) {
        // Load full JHA
        api(`/jhas/${existing.id}`).then(full => {
          setJha(full);
          setForm(prev => ({
            ...prev,
            logged_in_to: full.logged_in_to || '',
            dispatcher: full.dispatcher || '',
            high_energy_hazards: full.high_energy_hazards || [],
            hazards: full.hazards || [],
            change_management_completed: full.change_management_completed,
            change_outage_number: full.change_outage_number || '',
            critical_steps: full.critical_steps || '',
            ppe_selected: full.ppe_selected || [],
            allergy_awareness: full.allergy_awareness || '',
            allergy_type: full.allergy_type || '',
            allergy_action_plan: full.allergy_action_plan || '',
            general_hazards: full.general_hazards || [],
            hot_weather_applicable: full.hot_weather_applicable || false,
            hot_weather_checklist: full.hot_weather_checklist || [],
          }));
          if (full.status === 'pre_job_complete') setStep('signing');
          if (full.status === 'post_job_complete') setStep('done');
        });
      } else {
        api('/jhas', { method: 'POST', body: JSON.stringify({ work_item_id: workItemId }) })
          .then(newJha => setJha(newJha));
      }
    });
  }, [workItemId]);

  const save = async () => {
    if (!jha) return;
    setErr('');
    setBusy(true);
    try {
      await api(`/jhas/${jha.id}`, { method: 'PATCH', body: JSON.stringify(form) });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setErr('');
    setBusy(true);
    try {
      await save();
      const updated = await api(`/jhas/${jha.id}/submit`, { method: 'POST', body: JSON.stringify({}) });
      setJha(updated);
      setStep('signing');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const completePostJob = async () => {
    setErr('');
    setBusy(true);
    try {
      const updated = await api(`/jhas/${jha.id}/post-job`, { method: 'POST', body: JSON.stringify(postJob) });
      setJha(updated);
      setStep('done');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const refreshJha = () => {
    if (jha) api(`/jhas/${jha.id}`).then(setJha);
  };

  const addHazardRow = () => setForm(p => ({ ...p, hazards: [...p.hazards, { hazard: '', when_it_could_happen: '', control: '' }] }));
  const setHazardRow = (i, field, val) => setForm(p => {
    const next = [...p.hazards];
    next[i] = { ...next[i], [field]: val };
    return { ...p, hazards: next };
  });
  const removeHazardRow = (i) => setForm(p => ({ ...p, hazards: p.hazards.filter((_, idx) => idx !== i) }));

  const s = { border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden', marginBottom: 14 };

  if (!jha) return (
    <div style={{ color: '#64748b', padding: 40, textAlign: 'center' }}>Loading JHA…</div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 780, maxHeight: '95vh', display: 'flex', flexDirection: 'column', backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 10 }}>

        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 17 }}>Job Hazard Analysis (JHA)</div>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
              {jha.work_item_title || `Work Item ${workItemId}`} · {jha.station || jha.location_name || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {step === 'prejob' && (
              <a href={`${API}/jhas/${jha.id}/pdf?token=${localStorage.getItem('token')}`} target="_blank" rel="noreferrer"
                style={{ padding: '6px 12px', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, fontSize: 12, textDecoration: 'none' }}>
                PDF
              </a>
            )}
            <button onClick={onClose} style={{ padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer' }}>✕ Close</button>
          </div>
        </div>

        {/* Step tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #334155' }}>
          {[
            { key: 'prejob', label: 'Pre-Job' },
            { key: 'signing', label: 'Crew Signatures' },
            { key: 'postjob', label: 'Post-Job Review' },
          ].map(t => (
            <button key={t.key} onClick={() => step !== 'done' && setStep(t.key)} style={{
              padding: '11px 18px', border: 'none',
              borderBottom: step === t.key ? '2px solid #3b82f6' : '2px solid transparent',
              backgroundColor: 'transparent',
              color: step === t.key ? '#93c5fd' : '#64748b',
              cursor: 'pointer', fontSize: 13, fontWeight: step === t.key ? 800 : 600,
            }}>{t.label}</button>
          ))}
          {step === 'done' && <span style={{ marginLeft: 'auto', padding: '11px 18px', color: '#22c55e', fontSize: 13, fontWeight: 800 }}>✓ Complete</span>}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>

          {/* ── PRE-JOB ── */}
          {step === 'prejob' && (
            <>
              {/* Auto-filled header */}
              <div style={s}>
                <SectionHead title="Auto-Filled Header" />
                <SectionBody>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Conducted By"><input disabled value={jha.conducted_by_name || '—'} style={{ ...inp, backgroundColor: '#111827', color: '#64748b' }} /></Field>
                    <Field label="Station"><input disabled value={jha.station || '—'} style={{ ...inp, backgroundColor: '#111827', color: '#64748b' }} /></Field>
                    <Field label="GPS Coordinates"><input disabled value={jha.gps_lat ? `${jha.gps_lat}, ${jha.gps_lng}` : '—'} style={{ ...inp, backgroundColor: '#111827', color: '#64748b' }} /></Field>
                    <Field label="High Risk Facility">
                      <input disabled value={jha.high_risk_facility ? 'YES — High Risk' : 'No'} style={{ ...inp, backgroundColor: '#111827', color: jha.high_risk_facility ? '#ef4444' : '#64748b' }} />
                    </Field>
                    <Field label="NERC Compliance">
                      <input disabled value={jha.nerc_compliance ? 'YES — NERC Site' : 'No'} style={{ ...inp, backgroundColor: '#111827', color: jha.nerc_compliance ? '#f59e0b' : '#64748b' }} />
                    </Field>
                    <Field label="911 Directions"><input disabled value={jha.directions_911 || '—'} style={{ ...inp, backgroundColor: '#111827', color: '#64748b' }} /></Field>
                  </div>
                </SectionBody>
              </div>

              {/* Dispatcher / Logged In */}
              <div style={s}>
                <SectionHead title="Dispatcher / System" />
                <SectionBody>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Logged In To">
                      <select value={form.logged_in_to} onChange={e => setForm(p => ({ ...p, logged_in_to: e.target.value }))} style={inp}>
                        <option value="">— Select —</option>
                        {LOGGED_IN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Dispatcher Name">
                      <input value={form.dispatcher} onChange={e => setForm(p => ({ ...p, dispatcher: e.target.value }))} style={inp} />
                    </Field>
                  </div>
                  {form.logged_in_to === 'CICO' && (
                    <div style={{ marginTop: 12, padding: '10px 12px', backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6 }}>
                      {jha?.cico_acknowledged_at ? (
                        <div style={{ color: '#4ade80', fontSize: 13, fontWeight: 700 }}>
                          ✓ CICO checked in — {new Date(jha.cico_acknowledged_at).toLocaleTimeString()}
                        </div>
                      ) : (
                        <button onClick={async () => {
                          if (!jha) return;
                          const now = new Date().toISOString();
                          await api(`/jhas/${jha.id}`, { method: 'PATCH', body: JSON.stringify({ cico_acknowledged_at: now }) });
                          setJha(prev => ({ ...prev, cico_acknowledged_at: now }));
                          const cicoUrl = process.env.REACT_APP_CICO_URL;
                          if (cicoUrl) window.open(cicoUrl, '_blank');
                        }} style={{ padding: '8px 16px', backgroundColor: '#1d4ed8', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                          → Open CICO &amp; Check In
                        </button>
                      )}
                    </div>
                  )}
                </SectionBody>
              </div>

              {/* High Energy Hazards */}
              <div style={s}>
                <SectionHead title="High Energy Hazards Present" />
                <SectionBody>
                  <CheckGroup options={HIGH_ENERGY_OPTIONS} selected={form.high_energy_hazards} onChange={v => setForm(p => ({ ...p, high_energy_hazards: v }))} />
                </SectionBody>
              </div>

              {/* Hazard Analysis Table */}
              <div style={s}>
                <SectionHead title="Hazard Analysis" />
                <SectionBody>
                  {form.hazards.map((h, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'start' }}>
                      <input value={h.hazard} onChange={e => setHazardRow(i, 'hazard', e.target.value)} placeholder="Hazard" style={inp} />
                      <input value={h.when_it_could_happen} onChange={e => setHazardRow(i, 'when_it_could_happen', e.target.value)} placeholder="When it could happen" style={inp} />
                      <input value={h.control} onChange={e => setHazardRow(i, 'control', e.target.value)} placeholder="Control measure" style={inp} />
                      <button onClick={() => removeHazardRow(i)} style={{ padding: '8px 10px', backgroundColor: '#7f1d1d', color: '#fecaca', border: 'none', borderRadius: 6, cursor: 'pointer' }}>✕</button>
                    </div>
                  ))}
                  <button onClick={addHazardRow} style={{ padding: '7px 14px', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>+ Add Hazard Row</button>
                </SectionBody>
              </div>

              {/* Change Management */}
              <div style={s}>
                <SectionHead title="Change Management" />
                <SectionBody>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Change Management Completed">
                      <select value={form.change_management_completed == null ? '' : String(form.change_management_completed)} onChange={e => setForm(p => ({ ...p, change_management_completed: e.target.value === '' ? null : e.target.value === 'true' }))} style={inp}>
                        <option value="">N/A</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </Field>
                    <Field label="Outage / Change Number">
                      <input value={form.change_outage_number} onChange={e => setForm(p => ({ ...p, change_outage_number: e.target.value }))} style={inp} />
                    </Field>
                  </div>
                </SectionBody>
              </div>

              {/* Critical Steps */}
              <div style={s}>
                <SectionHead title="Critical Steps" />
                <SectionBody>
                  <textarea value={form.critical_steps} onChange={e => setForm(p => ({ ...p, critical_steps: e.target.value }))} placeholder="List the critical steps for this job…" style={ta} />
                </SectionBody>
              </div>

              {/* PPE */}
              <div style={s}>
                <SectionHead title="PPE Required" />
                <SectionBody>
                  <CheckGroup options={PPE_OPTIONS} selected={form.ppe_selected} onChange={v => setForm(p => ({ ...p, ppe_selected: v }))} />
                </SectionBody>
              </div>

              {/* Allergies */}
              <div style={s}>
                <SectionHead title="Allergy Awareness" />
                <SectionBody>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Allergy Awareness"><input value={form.allergy_awareness} onChange={e => setForm(p => ({ ...p, allergy_awareness: e.target.value }))} style={inp} /></Field>
                    <Field label="Allergy Type"><input value={form.allergy_type} onChange={e => setForm(p => ({ ...p, allergy_type: e.target.value }))} style={inp} /></Field>
                    <Field label="Action Plan" span><textarea value={form.allergy_action_plan} onChange={e => setForm(p => ({ ...p, allergy_action_plan: e.target.value }))} style={{ ...ta, gridColumn: '1 / -1' }} /></Field>
                  </div>
                </SectionBody>
              </div>

              {/* General Hazards */}
              <div style={s}>
                <SectionHead title="General Hazards (Job Plan)" />
                <SectionBody>
                  <CheckGroup options={GENERAL_HAZARDS} selected={form.general_hazards} onChange={v => setForm(p => ({ ...p, general_hazards: v }))} />
                </SectionBody>
              </div>

              {/* Hot Weather */}
              <div style={s}>
                <SectionHead title="Hot Weather" />
                <SectionBody>
                  <div style={{ marginBottom: 12 }}>
                    <Toggle checked={form.hot_weather_applicable} onChange={v => setForm(p => ({ ...p, hot_weather_applicable: v }))} label="Hot weather conditions applicable" />
                  </div>
                  {form.hot_weather_applicable && (
                    <CheckGroup options={HOT_WEATHER_ITEMS} selected={form.hot_weather_checklist} onChange={v => setForm(p => ({ ...p, hot_weather_checklist: v }))} />
                  )}
                </SectionBody>
              </div>

              {err && <div style={{ color: '#f87171', fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{err}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={save} disabled={busy} style={{ padding: '9px 18px', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>
                  Save Draft
                </button>
                <button onClick={submit} disabled={busy} style={{ padding: '9px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>
                  {busy ? 'Submitting…' : 'Submit Pre-Job →'}
                </button>
              </div>
            </>
          )}

          {/* ── SIGNATURES ── */}
          {step === 'signing' && (
            <>
              <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>
                All crew members must sign before work begins. Use PIN for verified digital signature, or enter manually for contractors.
              </div>
              {(jha.signatures || []).map(s => (
                <div key={s.id} style={{ padding: '10px 14px', backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: '#e2e8f0', fontWeight: 700 }}>{s.printed_name} {s.company ? `(${s.company})` : ''}</div>
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                      {s.pin_verified ? '✓ PIN Verified' : 'Manual'} · {s.signed_at ? new Date(s.signed_at).toLocaleTimeString() : '—'}
                    </div>
                  </div>
                  <span style={{ color: '#22c55e', fontSize: 18 }}>✓</span>
                </div>
              ))}
              <SignRow jhaId={jha.id} teamUsers={teamUsers} onSigned={refreshJha} />
              <div style={{ marginTop: 16 }}>
                <button onClick={() => setStep('postjob')} style={{ padding: '9px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>
                  Proceed to Post-Job Review →
                </button>
              </div>
            </>
          )}

          {/* ── POST-JOB ── */}
          {step === 'postjob' && (
            <>
              <div style={s}>
                <SectionHead title="Post-Job Review" />
                <SectionBody>
                  <div style={{ display: 'grid', gap: 14 }}>
                    <Field label="Logged Out of All Systems">
                      <select value={postJob.logged_out == null ? '' : String(postJob.logged_out)} onChange={e => setPostJob(p => ({ ...p, logged_out: e.target.value === '' ? null : e.target.value === 'true' }))} style={inp}>
                        <option value="">— Select —</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </Field>
                    <Field label="Job Performed as Expected">
                      <select value={postJob.job_performed_as_expected == null ? '' : String(postJob.job_performed_as_expected)} onChange={e => setPostJob(p => ({ ...p, job_performed_as_expected: e.target.value === '' ? null : e.target.value === 'true' }))} style={inp}>
                        <option value="">— Select —</option>
                        <option value="true">Yes</option>
                        <option value="false">No — explain below</option>
                      </select>
                    </Field>
                    <Field label="Comments / Deviations">
                      <textarea value={postJob.post_job_comments} onChange={e => setPostJob(p => ({ ...p, post_job_comments: e.target.value }))} style={ta} placeholder="Notes on how the job went, deviations, lessons learned…" />
                    </Field>
                  </div>
                </SectionBody>
              </div>
              {err && <div style={{ color: '#f87171', fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{err}</div>}
              <button onClick={completePostJob} disabled={busy} style={{ padding: '9px 20px', backgroundColor: '#22c55e', color: '#052e16', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800 }}>
                {busy ? 'Saving…' : 'Complete JHA'}
              </button>
            </>
          )}

          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <div style={{ color: '#22c55e', fontSize: 20, fontWeight: 800 }}>JHA Complete</div>
              <div style={{ color: '#64748b', fontSize: 13, marginTop: 8 }}>Pre-job and post-job sections are locked.</div>
              <a href={`${API}/jhas/${jha.id}/pdf?token=${localStorage.getItem('token')}`} target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', marginTop: 18, padding: '9px 20px', backgroundColor: '#1e293b', border: '1px solid #334155', color: '#93c5fd', borderRadius: 6, textDecoration: 'none', fontWeight: 700 }}>
                Download PDF
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
